/**
 * [C] <quantity-break-table>
 * Drives the PDP hardcoded quantity-break table (snippet
 * 'product-quantity-break-table'). It is purely a UI layer over the standard
 * quantity selector — no Shopify B2B price lists involved:
 *   - highlights the tier row matching the currently selected quantity
 *   - clicking / pressing Enter or Space on a row sets the quantity selector to
 *     that tier's starting quantity and fires the events the theme listens for
 *   - mirrors the active tier's pre-formatted prices into the PDP price hero
 *     (snippet 'product-price-table', matched by section id via data-hero-price)
 *     so the big price stays in sync with the selected tier — no money formatting
 *     happens here, every value is rendered server-side in Liquid.
 */
if (!customElements.get('quantity-break-table')) {
  customElements.define(
    'quantity-break-table',
    class QuantityBreakTable extends HTMLElement {
      connectedCallback() {
        this.rows = Array.from(this.querySelectorAll('.qbt__row'));
        this.input = this.resolveInput();
        this.hero = this.resolveHero();
        this.onQtyChange = this.onQtyChange.bind(this);

        this.rows.forEach((row) => {
          row.addEventListener('click', () => this.selectTier(row));
          row.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
              event.preventDefault();
              this.selectTier(row);
            }
          });
        });

        if (this.input) {
          this.input.addEventListener('change', this.onQtyChange);
          this.input.addEventListener('input', this.onQtyChange);
        }

        // Re-sync when the variant changes (quantity resets to its minimum).
        if (window.FoxTheme?.pubsub) {
          this.variantUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, () => {
            this.input = this.resolveInput();
            this.hero = this.resolveHero();
            this.highlight(this.currentQty());
          });
        }

        this.highlight(this.currentQty());
      }

      disconnectedCallback() {
        if (this.input) {
          this.input.removeEventListener('change', this.onQtyChange);
          this.input.removeEventListener('input', this.onQtyChange);
        }
        if (this.variantUnsubscriber) this.variantUnsubscriber();
      }

      resolveInput() {
        const byId = this.dataset.quantityInput && document.getElementById(this.dataset.quantityInput);
        if (byId) return byId;
        // Fallback: the quantity input within the same product section.
        const root = this.closest('product-info') || document;
        return root.querySelector('.quantity__input');
      }

      resolveHero() {
        const byId = this.dataset.heroPrice && document.getElementById(this.dataset.heroPrice);
        if (byId) return byId;
        // Fallback: the price hero within the same product section.
        const root = this.closest('product-info') || document;
        return root.querySelector('.hero-price');
      }

      currentQty() {
        return this.input ? parseInt(this.input.value, 10) || 0 : 0;
      }

      activeRowFor(qty) {
        let active = this.rows[0] || null;
        let bestMin = -Infinity;
        this.rows.forEach((row) => {
          const min = parseInt(row.dataset.tierMin, 10);
          if (!Number.isNaN(min) && qty >= min && min >= bestMin) {
            active = row;
            bestMin = min;
          }
        });
        return active;
      }

      highlight(qty) {
        const active = this.activeRowFor(qty);
        this.rows.forEach((row) => {
          const on = row === active;
          row.classList.toggle('is-active', on);
          if (on) {
            row.setAttribute('aria-current', 'true');
          } else {
            row.removeAttribute('aria-current');
          }
        });
        this.syncHero(active);
      }

      // Mirror the active tier's prices into the price hero. Values are already
      // money-formatted in Liquid; we only copy text, never compute.
      syncHero(row) {
        if (!this.hero || !row) return;
        const slots = {
          '[data-hero-main-price]': row.dataset.mainPrice,
          '[data-hero-main-tva]': row.dataset.mainTva,
          '[data-hero-sub-price]': row.dataset.subPrice,
          '[data-hero-sub-tva]': row.dataset.subTva,
        };
        Object.entries(slots).forEach(([selector, value]) => {
          if (value == null) return;
          const el = this.hero.querySelector(selector);
          if (el) el.textContent = value;
        });
      }

      onQtyChange() {
        this.highlight(this.currentQty());
      }

      selectTier(row) {
        const min = parseInt(row.dataset.tierMin, 10);
        if (Number.isNaN(min)) return;
        if (this.input) {
          this.input.value = min;
          this.input.dispatchEvent(new Event('input', { bubbles: true }));
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        this.highlight(min);
      }
    }
  );
}
